/**
 * DealerConnect Demo Application Entry Point
 *
 * This is the main entry point for the DealerConnect demo static website.
 * DealerConnect is loaded dynamically via the loader, making it easy to switch
 * between local builds and CDN versions.
 */

import { loadDealerConnect, DealerConnectLoaderFailureReason } from './dealerconnect-loader';
import { getDealerConnectVersion, loadDemoConfig } from './demo-config';

/**
 * Configuration for loading DealerConnect
 * Set `local: false` to load from CDN instead of local build
 */
const LOADER_CONFIG = {
  // Use local build (from distDealerConnect copied to demo dist)
  local: false,
  localPath: 'dealerconnect/dealerconnect-entrypoint.js',
  // CDN configuration (used when local: false)
  cdnBase: 'https://webmodule-softtech.azureedge.net/dealer-connect',
  version: ''
};

/**
 * Bootstrap the demo application
 * 1. Load DealerConnect dynamically
 * 2. Import and register the demo page component
 */
async function bootstrap(): Promise<void> {
  console.log('[DealerConnect Demo] Starting bootstrap...');

  // Load pipeline-provided demo settings (apiserver.json)
  await loadDemoConfig();

  // If we're using CDN mode, the version must come from apiserver.json.
  if (!LOADER_CONFIG.local) {
    LOADER_CONFIG.version = getDealerConnectVersion().trim();
    if (!LOADER_CONFIG.version) {
      const errorMessage =
        'dealerConnectVersion is not set in assets/apiserver.json. ' +
        'Provide it (pipeline-injected) or switch LOADER_CONFIG.local=true for local builds.';

      document.body.innerHTML = `
        <div style="padding: 2rem; font-family: system-ui, sans-serif;">
          <h1 style="color: #c00;">DealerConnect Load Error</h1>
          <p>${errorMessage}</p>
        </div>
      `;
      return;
    }
  }

  console.log('[DealerConnect Demo] Loading DealerConnect from:', LOADER_CONFIG.local ? 'local build' : 'CDN');

  // Load DealerConnect dynamically
  const result = await loadDealerConnect(LOADER_CONFIG);

  if (!result.success) {
    console.error('[DealerConnect Demo] Failed to load DealerConnect:', result.message);

    // Show error in the UI
    const errorMessage =
      result.failureReason === DealerConnectLoaderFailureReason.ScriptLoadFailed
        ? `Failed to load DealerConnect script from: ${result.source}`
        : `DealerConnect initialization failed: ${result.message}`;

    document.body.innerHTML = `
      <div style="padding: 2rem; font-family: system-ui, sans-serif;">
        <h1 style="color: #c00;">DealerConnect Load Error</h1>
        <p>${errorMessage}</p>
        <p>Check the browser console for more details.</p>
        <pre style="background: #f5f5f5; padding: 1rem; border-radius: 4px; overflow: auto;">
Failure Reason: ${result.failureReason}
Source: ${result.source ?? 'unknown'}
Error: ${result.error?.stack ?? result.message ?? 'Unknown error'}
        </pre>
      </div>
    `;
    return;
  }

  console.log('[DealerConnect Demo] DealerConnect loaded successfully');
  console.log('[DealerConnect Demo] Version:', result.api?.version);

  // Now import and register the demo page component
  // This dynamic import ensures DealerConnect is available before the component initializes
  await import('./main-page');

  console.log('[DealerConnect Demo] Demo page component registered');
}

// Start the bootstrap process
bootstrap().catch(error => {
  console.error('[DealerConnect Demo] Bootstrap failed:', error);
});
